import React, { useState, useRef } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Upload, X, Trash2, Plus, Pencil, ArrowUp, ArrowDown, Eye, EyeOff, ImageIcon, Copy } from "lucide-react";
import { toast } from "sonner";
import { CategoryIcon } from "../components/CategoryIcon";
import { AdminError, AdminEmpty } from "../components/AdminState";
import * as a from "../api";

const inp = "rounded-lg border border-slate-200 px-3 py-2 text-sm w-full outline-none focus:ring-2 focus:ring-secondary";
const lbl = "text-xs font-bold uppercase tracking-wide text-slate-400";

/* ---------------- REUSABLE MEDIA PICKER ---------------- */
export function MediaPicker({ value, onChange, label = "Immagine" }) {
  const [open, setOpen] = useState(false);
  const { data: media = [] } = useQuery({ queryKey: ["adminMedia"], queryFn: a.adminMedia, enabled: open });
  const qc = useQueryClient();
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const doUpload = async (e) => {
    const file = e.target.files[0]; if (!file) return;
    setUploading(true);
    try { const { url } = await a.uploadMedia(file); qc.invalidateQueries({ queryKey: ["adminMedia"] }); onChange(url); toast.success("Immagine caricata"); setOpen(false); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload fallito"); }
    finally { setUploading(false); }
  };
  return (
    <div>
      <span className={lbl}>{label}</span>
      <div className="mt-1 flex items-center gap-3">
        <div className="h-16 w-24 rounded-lg border overflow-hidden bg-slate-50 flex items-center justify-center shrink-0">
          {value ? <img src={value} alt="" className="h-full w-full object-cover" /> : <ImageIcon className="h-5 w-5 text-slate-300" />}
        </div>
        <div className="flex-1">
          <input className={inp} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="URL immagine o scegli dalla libreria" />
          <div className="mt-1.5 flex gap-2">
            <button type="button" onClick={() => setOpen(true)} data-testid="open-media-library" className="text-xs font-semibold text-primary">Scegli dalla libreria</button>
            {value && <button type="button" onClick={() => onChange("")} className="text-xs font-semibold text-slate-400">Rimuovi</button>}
          </div>
        </div>
      </div>
      {open && (
        <div className="fixed inset-0 z-[60] bg-black/50 flex items-start justify-center overflow-y-auto p-4" onClick={() => setOpen(false)}>
          <div className="bg-white rounded-2xl w-full max-w-3xl my-8 p-6" onClick={(e) => e.stopPropagation()} data-testid="media-picker-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg">Libreria Media</h3>
              <div className="flex gap-2">
                <button onClick={() => fileRef.current.click()} className="rounded-full bg-primary text-white px-4 py-1.5 text-sm font-semibold flex items-center gap-1"><Upload className="h-4 w-4" />{uploading ? "..." : "Carica"}</button>
                <button onClick={() => setOpen(false)}><X className="h-5 w-5 text-slate-400" /></button>
              </div>
              <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={doUpload} />
            </div>
            {media.length === 0 && <p className="text-sm text-slate-400">Nessuna immagine nella libreria. Carica la prima.</p>}
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 max-h-[60vh] overflow-y-auto">
              {media.map((m) => (
                <button key={m.id} onClick={() => { onChange(m.url); setOpen(false); }} className="relative h-24 rounded-lg overflow-hidden border hover:ring-2 ring-primary">
                  <img src={m.url} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ---------------- MEDIA LIBRARY TAB ---------------- */
export function MediaTab() {
  const qc = useQueryClient();
  const { data: media = [] } = useQuery({ queryKey: ["adminMedia"], queryFn: a.adminMedia });
  const fileRef = useRef();
  const [uploading, setUploading] = useState(false);
  const del = useMutation({ mutationFn: a.deleteMedia, onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminMedia"] }); toast.success("Immagine eliminata"); } });
  const doUpload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    try { for (const f of files) await a.uploadMedia(f); qc.invalidateQueries({ queryKey: ["adminMedia"] }); toast.success("Caricamento completato"); }
    catch (err) { toast.error(err.response?.data?.detail || "Upload fallito"); }
    finally { setUploading(false); e.target.value = ""; }
  };
  const copyUrl = (url) => { navigator.clipboard?.writeText(url); toast.success("URL copiato"); };
  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Carica e gestisci le immagini del sito. Usale per hero, categorie ed esperienze.</p>
        <button onClick={() => fileRef.current.click()} data-testid="media-upload-btn" className="rounded-full bg-primary text-white text-sm font-semibold px-5 py-2 flex items-center gap-1"><Upload className="h-4 w-4" />{uploading ? "Caricamento..." : "Carica immagini"}</button>
        <input ref={fileRef} type="file" accept="image/*" multiple className="hidden" onChange={doUpload} />
      </div>
      {media.length === 0 && <div className="bg-white rounded-xl border border-dashed border-slate-200 p-10 text-center text-slate-400" data-testid="media-empty">Nessuna immagine. Carica la prima immagine per iniziare.</div>}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {media.map((m) => (
          <div key={m.id} data-testid={`media-item-${m.id}`} className="group relative rounded-xl overflow-hidden border border-slate-200 bg-white">
            <div className="h-32 overflow-hidden"><img src={m.url} alt="" className="h-full w-full object-cover" /></div>
            <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
              <button onClick={() => copyUrl(m.url)} title="Copia URL" className="rounded-full bg-white/90 p-2"><Copy className="h-4 w-4 text-slate-700" /></button>
              <button onClick={() => { if (window.confirm("Eliminare questa immagine?")) del.mutate(m.id); }} title="Elimina" data-testid={`media-delete-${m.id}`} className="rounded-full bg-white/90 p-2"><Trash2 className="h-4 w-4 text-red-500" /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ---------------- CATEGORY EDITOR ---------------- */
function CategoryEditor({ cat, onClose }) {
  const qc = useQueryClient();
  const isNew = !cat?.slug;
  const [f, setF] = useState(cat ? { ...cat } : { slug: "", name_it: "", name_en: "", name_de: "", name_fr: "", icon: "Star", image: "", hidden: false });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = useMutation({
    mutationFn: () => (isNew ? a.createCategory(f) : a.updateCategory(cat.slug, f)),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminCategories"] }); qc.invalidateQueries({ queryKey: ["categories"] }); toast.success("Categoria salvata"); onClose(); },
    onError: (e) => toast.error(e.response?.data?.detail || "Errore"),
  });
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto p-4" data-testid="category-editor">
      <div className="bg-white rounded-2xl w-full max-w-xl my-8 p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold text-lg">{isNew ? "Nuova categoria" : "Modifica categoria"}</h3>
          <button onClick={onClose}><X className="h-5 w-5 text-slate-400" /></button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          {isNew && <label className="sm:col-span-2"><span className={lbl}>Slug (identificatore URL)</span><input data-testid="cat-slug" className={inp} value={f.slug} onChange={(e) => set("slug", e.target.value)} placeholder="es. escursioni-trekking" /></label>}
          <label><span className={lbl}>Nome (IT)</span><input data-testid="cat-name-it" className={inp} value={f.name_it} onChange={(e) => set("name_it", e.target.value)} /></label>
          <label><span className={lbl}>Nome (EN)</span><input className={inp} value={f.name_en} onChange={(e) => set("name_en", e.target.value)} /></label>
          <label><span className={lbl}>Nome (DE)</span><input className={inp} value={f.name_de} onChange={(e) => set("name_de", e.target.value)} /></label>
          <label><span className={lbl}>Nome (FR)</span><input className={inp} value={f.name_fr} onChange={(e) => set("name_fr", e.target.value)} /></label>
          <label className="flex items-end gap-2">
            <div className="flex-1"><span className={lbl}>Icona (lucide)</span><input className={inp} value={f.icon} onChange={(e) => set("icon", e.target.value)} placeholder="Waves, Ship, Car..." /></div>
            <div className="h-9 w-9 rounded-lg bg-primary/8 text-primary flex items-center justify-center"><CategoryIcon name={f.icon} /></div>
          </label>
          <label className="flex items-center gap-2 mt-5"><input type="checkbox" data-testid="cat-visible" checked={!f.hidden} onChange={(e) => set("hidden", !e.target.checked)} /> Visibile sul sito (ON = pubblicata)</label>
          <div className="sm:col-span-2"><MediaPicker value={f.image} onChange={(v) => set("image", v)} label="Immagine categoria (opzionale)" /></div>
        </div>
        <div className="flex justify-end gap-2 mt-6">
          <button onClick={onClose} className="rounded-full border px-5 py-2 text-sm font-semibold">Annulla</button>
          <button onClick={() => save.mutate()} disabled={save.isPending || (isNew && !f.slug) || !f.name_it} data-testid="cat-save-btn" className="rounded-full bg-primary text-white px-6 py-2 text-sm font-semibold disabled:opacity-40">Salva</button>
        </div>
      </div>
    </div>
  );
}

/* ---------------- CATEGORIES TAB ---------------- */
export function CategoriesTab() {
  const qc = useQueryClient();
  const { data: categories = [], isError, refetch } = useQuery({ queryKey: ["adminCategories"], queryFn: a.adminCategories });
  const [editing, setEditing] = useState(null);
  const del = useMutation({ mutationFn: a.deleteCategory, onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminCategories"] }); qc.invalidateQueries({ queryKey: ["categories"] }); toast.success("Categoria eliminata"); }, onError: (e) => toast.error(e.response?.data?.detail || "Errore") });
  const reorder = useMutation({ mutationFn: a.reorderCategories, onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminCategories"] }); qc.invalidateQueries({ queryKey: ["categories"] }); } });
  const toggleHidden = useMutation({ mutationFn: ({ slug, data }) => a.updateCategory(slug, data), onSuccess: () => { qc.invalidateQueries({ queryKey: ["adminCategories"] }); qc.invalidateQueries({ queryKey: ["categories"] }); } });

  const move = (idx, dir) => {
    const arr = categories.map((c) => c.slug);
    const j = idx + dir;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    reorder.mutate(arr);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <p className="text-sm text-slate-500">Crea, rinomina, riordina e nascondi le categorie. L'ordine si riflette sulla homepage e nei filtri.</p>
        <button onClick={() => setEditing({})} data-testid="new-category-btn" className="rounded-full bg-primary text-white text-sm font-semibold px-5 py-2 flex items-center gap-1"><Plus className="h-4 w-4" /> Nuova categoria</button>
      </div>
      {isError ? <AdminError onRetry={refetch} /> : categories.length === 0 ? <AdminEmpty message="Nessuna categoria." /> : (
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-slate-500 text-left"><tr>{["Ordine", "Immagine", "Nome (IT)", "Slug", "Esperienze", "Stato", "Azioni"].map((h) => <th key={h} className="px-3 py-3 font-semibold whitespace-nowrap">{h}</th>)}</tr></thead>
          <tbody className="divide-y divide-slate-100">
            {categories.map((c, i) => (
              <tr key={c.slug} data-testid={`category-row-${c.slug}`} className="hover:bg-slate-50">
                <td className="px-3 py-2">
                  <div className="flex flex-col">
                    <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-primary disabled:opacity-20"><ArrowUp className="h-3.5 w-3.5" /></button>
                    <button onClick={() => move(i, 1)} disabled={i === categories.length - 1} className="text-slate-400 hover:text-primary disabled:opacity-20"><ArrowDown className="h-3.5 w-3.5" /></button>
                  </div>
                </td>
                <td className="px-3 py-2">{c.image ? <img src={c.image} alt="" className="h-9 w-12 rounded object-cover" /> : <div className="h-9 w-9 rounded-lg bg-primary/8 text-primary flex items-center justify-center"><CategoryIcon name={c.icon} /></div>}</td>
                <td className="px-3 py-2 font-semibold">{c.name_it}</td>
                <td className="px-3 py-2 text-slate-400 font-mono text-xs">{c.slug}</td>
                <td className="px-3 py-2">{c.count}</td>
                <td className="px-3 py-2">
                  <button onClick={() => toggleHidden.mutate({ slug: c.slug, data: { ...c, hidden: !c.hidden } })} data-testid={`cat-toggle-${c.slug}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold ${c.hidden ? "bg-slate-100 text-slate-400" : "bg-emerald-50 text-emerald-600"}`}>
                    {c.hidden ? <><EyeOff className="h-3 w-3" /> Nascosta</> : <><Eye className="h-3 w-3" /> Visibile</>}
                  </button>
                </td>
                <td className="px-3 py-2 whitespace-nowrap">
                  <button onClick={() => setEditing(c)} data-testid={`edit-cat-${c.slug}`} title="Modifica" className="p-1.5 text-slate-400 hover:text-primary"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => { if (window.confirm("Eliminare questa categoria?")) del.mutate(c.slug); }} title="Elimina" data-testid={`delete-cat-${c.slug}`} className="p-1.5 text-slate-400 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}
      {editing && <CategoryEditor cat={editing.slug ? editing : null} onClose={() => setEditing(null)} />}
    </div>
  );
}
